import { Capacitor } from "@capacitor/core";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssessmentIcon from "@mui/icons-material/AssessmentOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import EastIcon from "@mui/icons-material/East";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import FeedbackIcon from "@mui/icons-material/FeedbackOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import MessageIcon from "@mui/icons-material/Message";
import PersonIcon from "@mui/icons-material/Person";
import SegmentIcon from "@mui/icons-material/Segment";
import SettingsIcon from "@mui/icons-material/Settings";
import { Avatar, Box, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, type SxProps, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Fragment, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useIsMobile } from "../../hooks/useMobile";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { ALPHA, INK, INK_BLACK, NEUTRAL, SURFACE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { AppPlatform, CAMPAIGN_FEEDBACK_FORM_URL, DEFAULT_FEEDBACK_FORM_URL, DepartmentNames, MobileStaffingView, SUPPORT_EMAIL } from "../../utils/models";
import { staffingWeeklyPath } from "../../utils/staffingRoutes";
import { useGapsViewPreference, useIsGapsViewActive, useIsGapsViewAvailable } from "../staffing/mobileGaps/useGapsViewPreference";
import { BetaBadge } from "../UIToolkit/Badges/BetaBadge";
import { AnnieMenuIcon } from "./AnnieMenuIcon";
import { AnnieMenuLabel } from "./AnnieMenuLabel";
import { headerStyles } from "./utils";
import { ViewSwitchMenuItem } from "./ViewSwitchMenuItem";

// Drawer-wide ink color used for icons, labels, and avatar fallback fill.
const DRAWER_INK = INK.SECONDARY;
const DRAWER_BORDER = NEUTRAL[300];
const textSx = { color: DRAWER_INK, fontSize: "16px" } as const satisfies SxProps;
const drawerListItemTextSx = { "& .MuiListItemText-primary": textSx } as const satisfies SxProps;
const drawerIconSx = { color: DRAWER_INK, alignItems: "center", fontSize: "18px" } as const satisfies SxProps;
const listIconSx = { width: "40px" } as const satisfies SxProps;
const listItemButtonSx = { borderRadius: 1 } as const satisfies SxProps;
// Secondary line shows the current view, styled down in size/opacity so it reads as context.
const drawerListItemSwitchTextSx = {
	"& .MuiListItemText-primary": textSx,
	"& .MuiListItemText-secondary": { color: DRAWER_INK, opacity: 0.7, fontSize: "13px" },
} as const satisfies SxProps;

interface MenuProps {
	setMenuOpen: (isOpen: boolean) => void;
	userInfo: {
		first_name?: string;
		last_name?: string;
		email?: string;
		username?: string;
	};
	open: boolean;
	profileImage: string | null;
	/** Opens full-screen Annie (mobile head-nurse menu). Undefined when Annie isn't available. */
	onOpenAnnie?: () => void;
	/**
	 * Which edge the menu slides in from. `"right"` is the hamburger everywhere in the app; `"bottom"` is
	 * the gaps-first shell's "More" slot, whose bottom bar replaced that hamburger.
	 *
	 * The gaps shell opens THIS component rather than a menu of its own on purpose: a second list of the
	 * same destinations is a list that goes stale the first time anyone adds a row to one of them.
	 */
	anchor?: "right" | "bottom";
	/**
	 * Rendered between the identity header and the menu's own rows — so who you are and the way out stay
	 * first, and this leads the list of places to go. The gaps-first sheet puts its quick actions here,
	 * without this component having to know what they are.
	 */
	prepend?: ReactNode;
	/** Held off the viewport floor by this much, so a fixed bottom bar stays visible under an anchored-bottom menu. */
	bottomOffset?: string;
	/** Lands on the Drawer root. MUI portals the paper out of the caller's tree, so a wrapper cannot be used to find it. */
	dataTestId?: string;
}

const AdminDrawer = ({ setMenuOpen, userInfo, open, profileImage, onOpenAnnie, anchor = "right", prepend, bottomOffset, dataTestId }: MenuProps) => {
	const formatPersonName = useFormatPersonName();
	const isGapsViewAvailable = useIsGapsViewAvailable();
	const isGapsViewActive = useIsGapsViewActive();
	const { setStoredView } = useGapsViewPreference();
	// Whether the gaps-first view is this department's default landing — only told to analytics, so a switch by someone
	// the flag had left on classic can be told apart from a return by someone who left the view.
	const isGapsViewDefault = useFeatureFlag(FEATURE_FLAGS.MobileGapsView);
	// Never offered from inside the gaps sheet itself (`anchor === "bottom"`), and never to someone the
	// view is not offered to. `!isGapsViewActive` gates the rest: this row is the way INTO the view, so it
	// belongs wherever the user is currently seeing classic — because they chose it, because the flag
	// leaves them on classic by default, or because they are on a URL that is pinned to classic regardless
	// of what they chose. Reading the stored preference directly missed the last case, which is exactly the
	// state that most needs a way out.
	const showSwitchToGaps = anchor === "right" && isGapsViewAvailable && !isGapsViewActive;
	const { t } = useTranslation();
	const { isMobile } = useIsMobile();
	const { logout, userInfo: authUserInfo } = useAuth();
	const navigate = useNavigate();
	const { chosenDepartment } = useHospitalData();
	// The InHouse campaign facility gets its own feedback form; every other dept keeps the default.
	const feedbackFormUrl =
		chosenDepartment?.department_id === DepartmentNames.BrightwaterSunriseMeadows ? CAMPAIGN_FEEDBACK_FORM_URL : DEFAULT_FEEDBACK_FORM_URL;
	const isCallOffEnabled = useFeatureFlag(FEATURE_FLAGS.CallOffAgent);
	const isSupportUser = authUserInfo?.support || false;
	// onOpenAnnie is the single Annie gate — Layout only passes it for mobile head nurses with the flag on.
	const showAnnieEntry = !!onOpenAnnie;

	const handleClose = () => {
		setMenuOpen(false);
	};

	const handleLogout = () => {
		setMenuOpen(false);
		trackEvent("logout clicked");
		logout();
		if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === AppPlatform.Android) {
			return;
		}
		window.location.reload();
	};

	return (
		<Drawer
			data-testid={dataTestId}
			anchor={anchor}
			open={open}
			onClose={handleClose}
			// The shared style pins `.MuiDrawer-paper` to 340px for the right-hand drawer, and that descendant
			// selector outranks anything passed through `slotProps.paper` — so a bottom sheet has to replace
			// it rather than add to it, or it renders 340px wide in the corner of the phone.
			sx={
				anchor === "bottom"
					? {
							"& .MuiDrawer-paper": {
								width: "100%",
								maxWidth: "100%",
								// Full height minus the bar it sits on. This menu is long — every row of the
								// hamburger plus the quick actions — so capping it shorter just hides rows behind
								// a scroll nobody knows is there.
								height: bottomOffset === undefined ? "100%" : `calc(100% - ${bottomOffset})`,
								maxHeight: bottomOffset === undefined ? "100%" : `calc(100% - ${bottomOffset})`,
								bgcolor: SURFACE,
								boxShadow: `0px -4px 8px ${alpha(INK_BLACK, ALPHA.SHADOW_SOFT)}`,
								borderTopLeftRadius: 16,
								borderTopRightRadius: 16,
								...(bottomOffset === undefined ? {} : { bottom: bottomOffset }),
							},
						}
					: headerStyles.drawer
			}
			slotProps={bottomOffset === undefined ? undefined : { backdrop: { sx: { bottom: bottomOffset } } }}
		>
			<Box
				sx={
					// A bottom sheet is full height, so its rows scroll — and a Close button that scrolls
					// away is a sheet you have to fling back to the top to dismiss. The identity row stays
					// put and only what is below it moves. The right-hand drawer is short enough not to
					// need this, so it keeps exactly the layout it shipped with.
					anchor === "bottom" ? { p: 2, pb: 0, display: "flex", flexDirection: "column", height: "100%", minHeight: 0 } : { p: 2 }
				}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 0,
						borderBottom: `1px solid ${DRAWER_BORDER}`,
						pb: 2,
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
						}}
					>
						{profileImage ? (
							<Avatar src={profileImage} sx={{ width: 40, height: 40 }} />
						) : (
							<Avatar sx={{ width: 40, height: 40, bgcolor: "primary.main" }}>
								<PersonIcon sx={{ color: "white" }} />
							</Avatar>
						)}
						<Box sx={{ ml: 2 }}>
							<Typography variant="subtitle1" sx={{ fontWeight: 700, color: DRAWER_INK }}>
								{/* DEFAULT: an account may have no first or last name; the missing part renders nothing, as before. */}
								{formatPersonName({ firstName: userInfo?.first_name ?? "", lastName: userInfo?.last_name ?? "" })}
							</Typography>
							<Typography
								variant="body2"
								sx={{
									color: DRAWER_INK,
									opacity: 0.7,
								}}
							>
								{userInfo?.email}
							</Typography>
						</Box>
					</Box>
					{/* The handler belongs on the BUTTON: on the icon it is unreachable by keyboard (the svg is not
					    focusable, so Enter on the button did nothing) and by any programmatic click, which
					    dispatches with the button as target and so never runs a handler on its child. */}
					<IconButton onClick={handleClose} aria-label={t("Close")}>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* Everything below the identity row scrolls; the row itself does not. */}
				<Box sx={anchor === "bottom" ? { flex: 1, minHeight: 0, overflowY: "auto", pb: 2 } : undefined}>
					{/* Between the identity header and the menu: who you are and the way out come first, then the
					    things you opened this for, then everywhere you could go instead. */}
					{prepend}

					<List sx={{ p: 0 }}>
						{showAnnieEntry && (
							<ListItemButton
								data-testid="admin-menu-annie"
								onClick={() => {
									setMenuOpen(false);
									onOpenAnnie?.();
								}}
								sx={listItemButtonSx}
							>
								<ListItemIcon sx={listIconSx}>
									<AnnieMenuIcon standardIcon={<AutoAwesomeIcon sx={drawerIconSx} />} />
								</ListItemIcon>
								<ListItemText
									primary={
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											<AnnieMenuLabel />
											<BetaBadge label={t("annie.beta.badge")} dataTestId="annie-nav-beta-badge" />
										</Box>
									}
									sx={drawerListItemTextSx}
								/>
							</ListItemButton>
						)}
						{!isMobile && (
							<>
								<ListItemButton
									data-testid="admin-menu-activity-log"
									onClick={() => {
										setMenuOpen(false);
										const path = `/${chosenDepartment.department_id}/activity-log`;
										navigate(path);
									}}
									sx={listItemButtonSx}
								>
									<ListItemIcon sx={listIconSx}>
										<AccessTimeIcon sx={drawerIconSx} />
									</ListItemIcon>
									<ListItemText primary={t("activity.log")} sx={drawerListItemTextSx} />
								</ListItemButton>

								<ListItemButton
									data-testid="admin-menu-employee-changelog"
									onClick={() => {
										setMenuOpen(false);
										navigate(`/${chosenDepartment.department_id}/employee-changelog`);
									}}
									sx={listItemButtonSx}
								>
									<ListItemIcon sx={listIconSx}>
										<SegmentIcon sx={drawerIconSx} />
									</ListItemIcon>
									<ListItemText primary={t("Employee Changelog")} sx={drawerListItemTextSx} />
								</ListItemButton>

								<ListItemButton
									data-testid="admin-menu-call-ins"
									data-annie-target="admin-menu-call-ins"
									onClick={() => {
										setMenuOpen(false);
										const path = `/${chosenDepartment.department_id}/call-ins`;
										navigate(path);
									}}
									sx={listItemButtonSx}
								>
									<ListItemIcon sx={listIconSx}>
										<EventBusyIcon sx={drawerIconSx} />
									</ListItemIcon>
									<ListItemText primary={t("call.ins")} sx={drawerListItemTextSx} />
								</ListItemButton>

								<Box data-testid="admin-menu-reports-group">
									<Box sx={{ display: "flex", alignItems: "center", px: 2, mt: 1 }}>
										<ListItemIcon sx={listIconSx}>
											<FolderOutlinedIcon sx={drawerIconSx} />
										</ListItemIcon>
										<Typography sx={textSx}>{t("Reports")}</Typography>
									</Box>
									<Box sx={{ ml: 3, my: 1, pl: 1, borderLeft: "1px solid", borderColor: "rgba(0, 0, 0, 0.15)" }}>
										{isCallOffEnabled && (
											<ListItemButton
												data-testid="admin-menu-shift-pickup-report"
												onClick={() => {
													setMenuOpen(false);
													const path = `/${chosenDepartment.department_id}/shift-pickup-report`;
													navigate(path);
												}}
												sx={listItemButtonSx}
											>
												<ListItemIcon sx={listIconSx}>
													<AssessmentIcon sx={drawerIconSx} />
												</ListItemIcon>
												<ListItemText primary={t("Short-Term Call-in Report")} sx={drawerListItemTextSx} />
											</ListItemButton>
										)}

										<ListItemButton
											data-testid="admin-menu-schedule-report"
											onClick={() => {
												setMenuOpen(false);
												const path = `/${chosenDepartment.department_id}/schedule`;
												navigate(path);
											}}
											sx={listItemButtonSx}
										>
											<ListItemIcon sx={listIconSx}>
												<CalendarTodayIcon sx={drawerIconSx} />
											</ListItemIcon>
											<ListItemText primary={t("Schedule Report")} sx={drawerListItemTextSx} />
										</ListItemButton>

										<ListItemButton
											data-testid="admin-menu-disciplinary-report"
											onClick={() => {
												setMenuOpen(false);
												const path = `/${chosenDepartment.department_id}/disciplinary-report`;
												navigate(path);
											}}
											sx={listItemButtonSx}
										>
											<ListItemIcon sx={listIconSx}>
												<AssessmentIcon sx={drawerIconSx} />
											</ListItemIcon>
											<ListItemText primary={t("Disciplinary Report")} sx={drawerListItemTextSx} />
										</ListItemButton>

										<ListItemButton
											data-testid="admin-menu-leakage-report"
											onClick={() => {
												setMenuOpen(false);
												navigate(`/${chosenDepartment.department_id}/leakage-report`);
											}}
											sx={listItemButtonSx}
										>
											<ListItemIcon sx={listIconSx}>
												<AssessmentIcon sx={drawerIconSx} />
											</ListItemIcon>
											<ListItemText
												primary={
													<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
														{t("leakage_report.title")}
														<BetaBadge label={t("leakage_report.beta")} dataTestId="leakage-report-beta-badge" />
													</Box>
												}
												sx={drawerListItemTextSx}
											/>
										</ListItemButton>
									</Box>
								</Box>
							</>
						)}

						{isMobile && (
							<Fragment>
								<ListItemButton
									data-testid="admin-menu-team"
									onClick={() => {
										setMenuOpen(false);
										const path = `/${chosenDepartment.department_id}/team`;
										navigate(path);
									}}
									sx={listItemButtonSx}
								>
									<ListItemIcon sx={listIconSx}>
										<Diversity3Icon sx={drawerIconSx} />
									</ListItemIcon>
									<ListItemText primary={t("Team")} sx={drawerListItemTextSx} />
								</ListItemButton>
								<ListItemButton
									data-testid="admin-menu-messages"
									onClick={() => {
										setMenuOpen(false);
										const path = `/${chosenDepartment.department_id}/messages`;
										navigate(path);
									}}
									sx={listItemButtonSx}
								>
									<ListItemIcon sx={listIconSx}>
										<MessageIcon sx={drawerIconSx} />
									</ListItemIcon>
									<ListItemText primary={t("chat")} sx={drawerListItemTextSx} />
								</ListItemButton>
							</Fragment>
						)}
						{isMobile && (
							<ListItemButton
								data-testid="admin-menu-requests"
								onClick={() => {
									setMenuOpen(false);
									navigate(`/${chosenDepartment.department_id}/requests`);
								}}
								sx={listItemButtonSx}
							>
								<ListItemIcon sx={listIconSx}>
									<EastIcon sx={drawerIconSx} />
								</ListItemIcon>
								<ListItemText primary={t("requests")} sx={drawerListItemTextSx} />
							</ListItemButton>
						)}
						{/* The way INTO the gaps-first shell, for someone looking at classic — whether they left the
					    view or the flag never opened it for them. Only where that view is actually offered (phone,
					    head nurse, department type), and never inside the gaps sheet itself — there it would point
					    at the view you are already in. */}
						{showSwitchToGaps && (
							<ListItemButton
								data-testid="admin-menu-switch-to-gaps"
								onClick={() => {
									setMenuOpen(false);
									setStoredView(MobileStaffingView.Gaps);
									trackEvent("gaps_view_reentered", { department_id: chosenDepartment.department_id, rollout_flag_on: isGapsViewDefault });
									navigate(staffingWeeklyPath(chosenDepartment.department_id));
								}}
								sx={listItemButtonSx}
							>
								<ListItemIcon sx={listIconSx}>
									<DashboardOutlinedIcon sx={drawerIconSx} />
								</ListItemIcon>
								<ListItemText primary={t("gaps.switchToGapsView")} sx={drawerListItemTextSx} />
							</ListItemButton>
						)}
						<ListItemButton
							data-testid="admin-menu-share-feedback"
							onClick={() => {
								setMenuOpen(false);
								// noopener,noreferrer: the new tab can't reach back to this page (prevents tabnabbing).
								window.open(feedbackFormUrl, "_blank", "noopener,noreferrer");
							}}
							sx={listItemButtonSx}
						>
							<ListItemIcon sx={listIconSx}>
								<FeedbackIcon sx={drawerIconSx} />
							</ListItemIcon>
							<ListItemText primary={t("share.feedback")} sx={drawerListItemTextSx} />
						</ListItemButton>

						{/* Only shows for dual-role users; self-gates via useViewMode. */}
						<ViewSwitchMenuItem
							dataTestId="admin-menu-switch-view"
							onBeforeSwitch={() => setMenuOpen(false)}
							buttonSx={listItemButtonSx}
							iconContainerSx={listIconSx}
							iconSx={drawerIconSx}
							textSx={drawerListItemSwitchTextSx}
							showCurrentView
						/>

						<ListItemButton
							data-testid="admin-menu-settings"
							onClick={() => {
								setMenuOpen(false);
								const path = `/${chosenDepartment.department_id}/settings`;
								navigate(path);
							}}
							sx={listItemButtonSx}
						>
							<ListItemIcon sx={listIconSx}>
								<SettingsIcon sx={drawerIconSx} />
							</ListItemIcon>
							<ListItemText primary={t("Settings")} sx={drawerListItemTextSx} />
						</ListItemButton>

						<ListItemButton
							data-testid="admin-menu-support"
							onClick={() => {
								trackEvent("support clicked");
								window.location.href = `mailto:${SUPPORT_EMAIL}`;
							}}
							sx={listItemButtonSx}
						>
							<ListItemIcon sx={listIconSx}>
								<HelpOutlineIcon sx={drawerIconSx} />
							</ListItemIcon>
							<ListItemText primary={t("support")} sx={drawerListItemTextSx} />
						</ListItemButton>

						{isSupportUser && (
							<ListItemButton
								data-testid="admin-menu-onboarding-tools"
								onClick={() => {
									setMenuOpen(false);
									const path = `/${chosenDepartment.department_id}/onboarding/tools`;
									navigate(path);
								}}
								sx={listItemButtonSx}
							>
								<ListItemIcon sx={listIconSx}>
									<FileUploadOutlinedIcon sx={drawerIconSx} />
								</ListItemIcon>
								<ListItemText primary={t("Onboarding Tools")} sx={drawerListItemTextSx} />
							</ListItemButton>
						)}

						<ListItemButton onClick={handleLogout} data-testid="admin-menu-logout" sx={listItemButtonSx}>
							<ListItemIcon sx={listIconSx}>
								<LogoutIcon sx={drawerIconSx} />
							</ListItemIcon>
							<ListItemText primary={t("Logout")} sx={drawerListItemTextSx} />
						</ListItemButton>
					</List>
				</Box>
			</Box>
		</Drawer>
	);
};

export default AdminDrawer;
